import type {
  BrandSpecial,
  BrandSpecialQualifyingStatus,
} from '@iheartjane/data-access';
import {
  SpecialCallout,
  getSpecialApplicableWeights,
  getSpecialSlug,
} from '@iheartjane/menu';
import { Flex } from '@iheartjane/reefer';
import type { PriceId } from '@iheartjane/types';
import { useMemo } from 'react';
import { useNavigate } from 'react-router';

import { useFullDestination } from '@jane/bloom/hooks/navigation/useFullDestination';
import type { BrandDiscount } from '@jane/bloom/types/schemas/bloom/checkout';

interface JaneGoldCalloutSectionProps {
  brandSpecial: BrandSpecial;
  cartBrandDiscounts: BrandDiscount[];
  inStockWeights: PriceId[];
  isSegmentedDeal: boolean;
  isSignedIn: boolean;
  qualifyingStatus?: BrandSpecialQualifyingStatus;
}

export const JaneGoldCalloutSection = ({
  brandSpecial,
  cartBrandDiscounts,
  inStockWeights,
  isSegmentedDeal,
  isSignedIn,
  qualifyingStatus,
}: JaneGoldCalloutSectionProps) => {
  const generatePath = useFullDestination();
  const navigate = useNavigate();
  const isBundle = brandSpecial?.special_type !== 'product';

  const isQualified =
    (isBundle && qualifyingStatus?.qualifying_status === 'qualified') ||
    !isBundle;

  const isApplied = cartBrandDiscounts?.some(
    (discount) => discount.brand_special_id === brandSpecial.id
  );

  const linkPath = useMemo(
    () =>
      generatePath(
        `/jane-gold/${brandSpecial.id}/${getSpecialSlug(brandSpecial.title)}`
      ),
    [generatePath, brandSpecial.id, brandSpecial.title]
  );

  const applicableWeights = useMemo(
    () =>
      getSpecialApplicableWeights({
        applicableWeights: brandSpecial.applicable_weights,
        inStockWeights,
      }),
    [brandSpecial.applicable_weights, inStockWeights]
  );

  return (
    <Flex py={8}>
      <SpecialCallout
        applicableWeights={applicableWeights}
        brandImage={brandSpecial.product_brand?.logo_url ?? null}
        brandName={brandSpecial.product_brand?.name ?? undefined}
        isApplied={Boolean(isApplied)}
        isAuthenticated={isSignedIn}
        isGold={true}
        isQualified={isQualified}
        onClick={() => navigate(linkPath)}
        segmented={isSegmentedDeal}
        specialTitle={brandSpecial.title}
        specialType={isBundle ? 'bundle' : 'product'}
        variant="pdp"
      />
    </Flex>
  );
};
